/**
 * This file includes polyfills needed by Angular and is loaded before the app.
 * You can add your own extra polyfills to this file.
 *
 * This file is divided into 2 sections:
 *   1. Browser polyfills. These are applied before loading ZoneJS and are sorted by browsers.
 *   2. Application imports. Files imported after ZoneJS that should be loaded before your main
 *      file.
 *
 * The current setup is for so-called "evergreen" browsers; the last versions of browsers that
 * automatically update themselves. This includes Safari >= 10, Chrome >= 55 (including Opera),
 * Edge >= 13 on the desktop, and iOS 10 and Chrome on mobile.
 *
 * Learn more in https://angular.io/guide/browser-support
 */

/***************************************************************************************************
 * BROWSER POLYFILLS
 */

/***************************************************************************************************
 * iOS 15 / Safari 15 兼容性 Polyfills
 *
 * Angular 20 以 ES2022 为编译目标，以下 API 在 Safari 15.0-15.3 中不可用：
 *  - Array.prototype.at()          (Safari 15.4+)
 *  - String.prototype.at()         (Safari 15.4+)
 *  - TypedArray.prototype.at()     (Safari 15.4+)
 *  - Object.hasOwn()               (Safari 15.4+)
 *  - Array.prototype.findLast()    (Safari 15.4+)
 *  - Array.prototype.findLastIndex() (Safari 15.4+)
 *  - structuredClone()             (Safari 15.4+)
 *  - AbortSignal.timeout()         (Safari 16.4+)
 *
 * 注意：ES2022 语法降级（如 class static blocks）由 Babel 通过 browserslist 配置自动处理。
 *       此处仅处理 API 级别的 polyfill。
 ***************************************************************************************************/

// Array.prototype.at() — Safari 15.4+
if (!Array.prototype.at) {
  Object.defineProperty(Array.prototype, 'at', {
    value: function (n: number) {
      n = Math.trunc(n) || 0;
      if (n < 0) { n += this.length; }
      if (n < 0 || n >= this.length) { return undefined; }
      return this[n];
    },
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

// String.prototype.at() — Safari 15.4+
if (!String.prototype.at) {
  Object.defineProperty(String.prototype, 'at', {
    value: function (n: number) {
      n = Math.trunc(n) || 0;
      if (n < 0) { n += this.length; }
      if (n < 0 || n >= this.length) { return undefined; }
      return this.charAt(n);
    },
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

// TypedArray.prototype.at() — Safari 15.4+
if (typeof Int8Array !== 'undefined' && !Int8Array.prototype.at) {
  const TypedArrayProto = Object.getPrototypeOf(Int8Array.prototype);
  if (TypedArrayProto) {
    Object.defineProperty(TypedArrayProto, 'at', {
      value: function (n: number) {
        n = Math.trunc(n) || 0;
        if (n < 0) { n += this.length; }
        if (n < 0 || n >= this.length) { return undefined; }
        return this[n];
      },
      writable: true,
      enumerable: false,
      configurable: true,
    });
  }
}

// Object.hasOwn() — Safari 15.4+
if (!(Object as any).hasOwn) {
  (Object as any).hasOwn = function (obj: any, prop: PropertyKey): boolean {
    return Object.prototype.hasOwnProperty.call(obj, prop);
  };
}

// Array.prototype.findLast() — Safari 15.4+
if (!(Array.prototype as any).findLast) {
  Object.defineProperty(Array.prototype, 'findLast', {
    value: function (callback: Function, thisArg?: any) {
      for (let i = this.length - 1; i >= 0; i--) {
        if (callback.call(thisArg, this[i], i, this)) { return this[i]; }
      }
      return undefined;
    },
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

// Array.prototype.findLastIndex() — Safari 15.4+
if (!(Array.prototype as any).findLastIndex) {
  Object.defineProperty(Array.prototype, 'findLastIndex', {
    value: function (callback: Function, thisArg?: any) {
      for (let i = this.length - 1; i >= 0; i--) {
        if (callback.call(thisArg, this[i], i, this)) { return i; }
      }
      return -1;
    },
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

// structuredClone() — Safari 15.4+
if (typeof (window as any).structuredClone !== 'function') {
  (window as any).structuredClone = function (value: any): any {
    // 简单实现：通过 JSON 序列化实现深拷贝
    // 对于 Date / RegExp / Blob 等类型不完全兼容，但满足 Angular 框架内部使用
    if (value === undefined) { return undefined; }
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  };
}

// AbortSignal.timeout() — Safari 16.4+ (Angular HttpClient 可能使用)
if (typeof AbortSignal !== 'undefined' && !(AbortSignal as any).timeout) {
  (AbortSignal as any).timeout = function (ms: number): AbortSignal {
    const controller = new AbortController();
    setTimeout(() => {
      controller.abort(new DOMException('The operation was aborted due to timeout', 'TimeoutError'));
    }, ms);
    return controller.signal;
  };
}

// AbortSignal.any() — Safari 17.4+ (某些第三方库可能使用)
if (typeof AbortSignal !== 'undefined' && !(AbortSignal as any).any) {
  (AbortSignal as any).any = function (signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();
    for (const signal of signals) {
      if (signal.aborted) {
        controller.abort(signal.reason);
        return controller.signal;
      }
      signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
    }
    return controller.signal;
  };
}

// requestIdleCallback — Safari 不支持直到 16.1
// 企业微信 WebView 基于 Safari，iOS 15 上不存在此 API
if (typeof (window as any).requestIdleCallback !== 'function') {
  (window as any).requestIdleCallback = function (cb: Function, options?: any): number {
    const timeout = options && options.timeout ? options.timeout : 50;
    const start = Date.now();
    return setTimeout(() => {
      cb({
        didTimeout: false,
        timeRemaining: () => Math.max(0, timeout - (Date.now() - start)),
      });
    }, 1) as unknown as number;
  };
  (window as any).cancelIdleCallback = function (id: number): void {
    clearTimeout(id);
  };
}

// globalThis 兼容 (Safari 12.1+，理论上 iOS 15 已支持，但作为安全保障)
if (typeof globalThis === 'undefined') {
  (window as any).globalThis = window;
}

/***************************************************************************************************
 * END iOS 15 / Safari 15 兼容性 Polyfills
 ***************************************************************************************************/

/**
 * By default, zone.js will patch all possible macroTask and DomEvents
 * user can disable parts of macroTask/DomEvents patch by setting following flags
 * because those flags need to be set before `zone.js` being loaded, and webpack
 * will put import in the top of bundle, so user need to create a separate file
 * in this directory (for example: zone-flags.ts), and put the following flags
 * into that file, and then add the following code before importing zone.js.
 * import './zone-flags.ts';
 *
 * The flags allowed in zone-flags.ts are listed here.
 *
 * The following flags will work for all browsers.
 *
 * (window as any).__Zone_disable_requestAnimationFrame = true; // disable patch requestAnimationFrame
 * (window as any).__Zone_disable_on_property = true; // disable patch onProperty such as onclick
 * (window as any).__zone_symbol__BLACK_LISTED_EVENTS = ['scroll', 'mousemove']; // disable patch specified eventNames
 *
 *  in IE/Edge developer tools, the addEventListener will also be wrapped by zone.js
 *  with the following flag, it will bypass `zone.js` patch for IE/Edge
 *
 *  (window as any).__Zone_enable_cross_context_check = true;
 *
 */

import './zone-flags';

/***************************************************************************************************
 * Zone JS is required by default for Angular itself.
 */

import 'zone.js';  // Included with Angular CLI.

(window as any).global = window;


/***************************************************************************************************
 * APPLICATION IMPORTS
 */
